'use strict';

var index = require('../variants/index.js');
var big = require('./big.js');
var numberFormat = require('./numberFormat.js');

/**
 * Format the fraction parts of the number.
 */ const formatFractionAndDecimalParts = ({ numberParts, decimalValue, denomination, padded })=>{
    const newParts = [];
    if (numberParts.get.fraction) {
        // we need to set the `decimalValue` in the formatted number parts
        newParts.push({
            type: 'fraction',
            value: (decimalValue ?? '') + denomination
        });
        if (!padded && !decimalValue && numberParts.get.decimal) {
            // if we don't have a decimal value, remove the '.' string in the formatted number parts
            newParts.push({
                type: 'decimal',
                value: ''
            });
        }
    }
    return newParts;
};
/**
 * Add a whitespace between the sign and the amount.
 */ const formatSignParts = (numberParts)=>{
    if (numberParts.get.minusSign) {
        // add a whitespace between minus sign and amount
        return {
            type: 'minusSign',
            value: `${numberParts.get.minusSign} `
        };
    }
    if (numberParts.get.plusSign) {
        // add a whitespace between plus sign and amount
        return {
            type: 'plusSign',
            value: `${numberParts.get.plusSign} `
        };
    }
    return undefined;
};
/**
 * Determines if a manual sign (+ or -) should be added to the formatted amount.
 * This is needed when the sign isn't automatically added by NumberFormat,
 * typically for numbers between -1 and 1. We also handle the case where
 * the `prefixSymbol` is set to `<` and the `signDisplay` is set to 'never'
 * to avoid the sign being preceded by the prefixSymbol.
 */ const addManualSign = ({ amount, numberParts, showSign, prefixSymbol })=>{
    const hasExistingSign = numberParts.get.minusSign || numberParts.get.plusSign;
    const numAmount = Number(amount);
    const isNegative = numAmount < 0;
    const isPositive = numAmount > 0;
    // Return early if the sign is already present and the prefixSymbol is not '<'
    // or if the number is positive and showSign is false
    if (hasExistingSign && prefixSymbol !== '<' || !isNegative && !showSign) {
        return '';
    }
    // When negative, always show minus sign. This is the default
    // of the 'auto' signDisplay option. In the case of '<' prefixSymbol,
    // we set the signDisplay to 'never' and manually add the sign.
    if (isNegative) {
        return '- ';
    }
    // When positive and showSign is true, show plus sign
    if (isPositive && showSign) {
        return '+ ';
    }
    return '';
};
/**
 * Get the `signDisplay` option for `Intl.NumberFormat`.
 * If the `prefixSymbol` is set to `<`, we will set the `signDisplay`
 * to `never` because it will be manually added later in `addManualSign`.
 */ const getSignDisplayOption = ({ signDisplay, prefixSymbol })=>{
    if (prefixSymbol === '<') {
        return 'never';
    }
    return signDisplay ? 'exceptZero' : 'auto';
};
/**
 * Money formatter.
 */ const moneyFormat = (number, variant, locale, options)=>{
    if (isNaN(Number(number))) {
        // eslint-disable-next-line no-console
        console.warn(`useMoney: The value '${number}' is not a number or integer`);
        return;
    }
    const currency = typeof variant === 'string' ? index.default[variant] : variant;
    // When passing in a custom currency configuration, `name` is defined as
    // the shorthand currency code (i.e. 'BTC', 'ETH', etc.).
    const currencyCode = typeof variant === 'string' ? variant : variant.name;
    const moneyOptions = {
        ...index.defaultMoneyOptions,
        ...options
    };
    const padded = moneyOptions.padded ?? false;
    const { amount, decimalPlaces, denomination, prefixSymbol } = numberFormat.getFormatParts(number, currency.decimals ?? 2, options?.condensed);
    const metaOptions = {
        locale,
        style: 'currency',
        currency: currency.isCryptoCurrency ? 'USD' : currency.name,
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
        currencyDisplay: currency.isCryptoCurrency && !currency.symbol ? 'name' : 'symbol',
        signDisplay: getSignDisplayOption({
            signDisplay: moneyOptions.signDisplay,
            prefixSymbol
        })
    };
    // Intl.NumberFormat does not handle big numbers formatting, however we still want to use it because it does formatting by locale.
    // Work around, we will let Intl.NumberFormat format only the "integer" portion of a number passed in and combine the formatted integer value with the decimal value
    const { stringNumValue, decimalValue } = big.convertBigNumber({
        number: amount,
        decimalPlaces: decimalPlaces,
        padded
    });
    // Use Intl to format the number in the given locale.
    const numberParts = numberFormat.NumberFormatHelper(Number(stringNumValue), metaOptions);
    const fractionAndDecimalParts = formatFractionAndDecimalParts({
        numberParts,
        decimalValue,
        denomination,
        padded
    });
    fractionAndDecimalParts.forEach(({ type, value })=>numberParts.set(type, value));
    // Replace the currency symbol with the given symbol or name
    if (currency.isCryptoCurrency) {
        numberParts.set('currency', currency.symbol ?? currency.name);
    }
    // Remove currency symbol when it's off
    if (!moneyOptions.symbol) {
        numberParts.remove('currency');
    }
    const signParts = formatSignParts(numberParts);
    if (signParts !== undefined) {
        numberParts.set(signParts.type, signParts.value);
    }
    // Manually add the sign if the number is between -1 and 1
    // Will return empty string if not applicable
    const manualSign = addManualSign({
        amount,
        numberParts,
        showSign: moneyOptions.signDisplay,
        prefixSymbol
    });
    return {
        formattedMoney: `${manualSign}${prefixSymbol}${numberParts.format()}`,
        numberParts,
        currencyCode: currencyCode,
        meta: metaOptions
    };
};

exports.moneyFormat = moneyFormat;
//# sourceMappingURL=moneyFormat.js.map
